import { useQueries } from '@tanstack/react-query'
import { QueryKeys } from 'constants/query-keys'
import { TIME } from 'utils/misc'
import { useAnalytics } from 'utils/analytics'
import { fetchMyTargetPerformanceStores } from 'queries/myTarget-performance/performance-metrics'
import { MPMEnvType } from 'utils/env'
import { TableData } from 'utils/data'
import { DETAIL_TIME_DIMENSIONS } from 'pages/myTarget-performance/constants'

interface UseMyTargetPerformanceDetailStoresParams {
    env: MPMEnvType
    accessToken: string
    location: string
    tab: string
    enabled?: boolean
}

interface UseMyTargetPerformanceDetailStoresResult {
    dataByTimeDimension: Record<string, TableData | undefined>
    isLoading: boolean
    isError: boolean
    error: unknown
    refetch: () => void
}

export function useMyTargetPerformanceDetailStores({
    env,
    accessToken,
    location,
    tab,
    enabled = true,
}: UseMyTargetPerformanceDetailStoresParams): UseMyTargetPerformanceDetailStoresResult {
    const { trackEvent } = useAnalytics()
    const { tenMinutes } = TIME

    const queries = DETAIL_TIME_DIMENSIONS.map((timeDimension) => {
        const filters = {
            location,
            tab,
            time_dimension: timeDimension,
        }

        return {
            queryKey: [
                QueryKeys.MY_TARGET_PERFORMANCE_DETAIL_STORES,
                location,
                tab,
                timeDimension,
            ],
            queryFn: () =>
                fetchMyTargetPerformanceStores({
                    env,
                    accessToken,
                    method: 'GET',
                    filters,
                    trackEvent,
                }),
            staleTime: tenMinutes,
            cacheTime: tenMinutes,
            retry: false,
            enabled: enabled && !!location,
        }
    })

    const results = useQueries({ queries })

    const dataByTimeDimension = results.reduce(
        (acc, result, index) => {
            const timeDimension = DETAIL_TIME_DIMENSIONS[index]
            acc[timeDimension] = result.data
            return acc
        },
        {} as Record<string, TableData | undefined>,
    )

    const isLoading = results.some((result) => result.isLoading)
    const isError = results.some((result) => result.isError)
    const error = results.find((result) => result.error)?.error

    const refetch = () => {
        results.forEach((result) => result.refetch())
    }

    return {
        dataByTimeDimension,
        isLoading,
        isError,
        error,
        refetch,
    }
}
